import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import type { TrialBalanceColumnsResponse } from '../../lib/types/finance.js';

/** Closing balance of one account, integer-Rial digit strings (never `Number`). */
export interface CoaBalance {
  readonly debitRial: string;
  readonly creditRial: string;
}

export type CoaBalancesState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly httpStatus: number }
  | {
      readonly status: 'ready';
      /** account code → closing balance; an account with no posted activity has no entry (balance 0). */
      readonly byCode: ReadonlyMap<string, CoaBalance>;
      readonly totals: CoaBalance;
      readonly balanced: boolean;
    };

/**
 * Real per-account balances for the chart-of-accounts tree.
 *
 * Source: `GET .../finance/reports/trial-balance-columns`
 * (apps/api/src/finance/reports.controller.ts:287) — posted-only, official,
 * up to today. Without `levelSequence` it returns each account's own posted
 * balance; with `levelSequence=N` the **server** rolls every deeper account
 * up into its level-N ancestor (packages/modules/accounting/src/journal/
 * ledger.ts `target()`), and a row whose own `levelSequence === N` is that
 * account's full-subtree balance. So group/کل balances come from the server
 * (levels 1-3), postable-level balances from the un-rolled query — the
 * client sums nothing (same "this page does no accounting" rule as
 * TrialBalancePage.tsx). Totals + `balanced` are the un-rolled query's own.
 *
 * Accounts nested deeper than level 4 would show only their own postings
 * (the rollup form is requested for levels 1-3); the default chart and the
 * create form's level picker stop at 4.
 */
export function useCoaBalances(
  slug: string | undefined,
  base: string,
  refreshKey: number,
): CoaBalancesState {
  const today = systemClock.today();
  const path = (level?: number): string | null =>
    slug
      ? `${base}/api/v1/finance/reports/trial-balance-columns?toDate=${today}${
          level ? `&levelSequence=${level}` : ''
        }`
      : null;
  const flat = useApiGet<TrialBalanceColumnsResponse>(path(), [slug, today, refreshKey]);
  const l1 = useApiGet<TrialBalanceColumnsResponse>(path(1), [slug, today, refreshKey]);
  const l2 = useApiGet<TrialBalanceColumnsResponse>(path(2), [slug, today, refreshKey]);
  const l3 = useApiGet<TrialBalanceColumnsResponse>(path(3), [slug, today, refreshKey]);

  const all = [flat, l1, l2, l3] as const;
  for (const r of all) {
    if (r.status === 'error') return { status: 'error', httpStatus: r.error.status };
  }
  if (
    flat.status !== 'ready' ||
    l1.status !== 'ready' ||
    l2.status !== 'ready' ||
    l3.status !== 'ready'
  ) {
    return { status: 'loading' };
  }

  const byCode = new Map<string, CoaBalance>();
  for (const row of flat.data.rows) {
    byCode.set(row.accountRef, {
      debitRial: row.closingDebitRial,
      creditRial: row.closingCreditRial,
    });
  }
  const rolled = [
    [1, l1],
    [2, l2],
    [3, l3],
  ] as const;
  for (const [level, res] of rolled) {
    for (const row of res.data.rows) {
      if (row.levelSequence !== level) continue;
      byCode.set(row.accountRef, {
        debitRial: row.closingDebitRial,
        creditRial: row.closingCreditRial,
      });
    }
  }
  return {
    status: 'ready',
    byCode,
    totals: {
      debitRial: flat.data.totals.closingDebitRial,
      creditRial: flat.data.totals.closingCreditRial,
    },
    balanced: flat.data.balanced,
  };
}
